import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface TopScreen {
  id: number;
  name: string;
  access_count: number;
  establishment_name: string;
}

interface DashboardStats {
  // Estadísticas globales (admin/management)
  total_clients?: number;
  active_clients?: number;
  inactive_clients?: number;
  total_establishments?: number;
  active_establishments?: number;
  inactive_establishments?: number;
  total_users?: number;
  active_users?: number;
  this_month_clients?: number;
  this_month_establishments?: number;
  total_screens?: number;
  active_screens?: number;
  inactive_screens?: number;
  total_products?: number;
  total_banners?: number;
  total_fonts?: number;
  total_icons?: number;
  total_tariffs?: number;
  establishments_without_screens?: number;
  establishments_without_tariffs?: number;
  this_month_screens?: number;
  total_accesses?: number;
  top_screens?: TopScreen[];
  
  // Estadísticas de client
  // (total_screens, total_products, total_tariffs ya están arriba)
  
  // Estadísticas de establishment
  establishment_name?: string;
  establishment_active?: number;
}

export const Management: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { canView } = usePermissions();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) {
        setLoadingStats(false);
        return;
      }
      
      try {
        const res = await apiFetch('dashboard/management/stats', {}, token);
        
        // Si es error de red, mantener stats actuales (null) y solo loggear
        if (isNetworkErrorResponse(res)) {
          console.warn('Error de red cargando estadísticas. Intentando de nuevo...');
          setLoadingStats(false);
          return;
        }

        const json = await parseJsonResponse(res);
        
        if (json.data && !json.error) {
          setStats(json.data);
        }
      } catch (error: any) {
        // Error de red o parsing - no cerrar sesión, solo loggear
        if (isNetworkError(error)) {
          console.warn('Error de conexión cargando estadísticas. Los datos se mantendrán.');
        } else {
        console.error('Error cargando estadísticas:', error);
        }
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [token]);

  const dashboardCards = [
    ...(canView('dashboard.clients_card') ? [{
      title: 'Clientes',
      subtitle: 'Gestionar clientes y cuentas',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      onClick: () => navigate('/management/clients'),
      gradient: 'from-blue-600 to-blue-700',
      description: 'Administra los clientes del sistema'
    }] : []),
    ...(canView('dashboard.establishments_card') ? [{
      title: 'Establecimientos',
      subtitle: 'Gestionar establecimientos',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      onClick: () => navigate('/management/establishments'),
      gradient: 'from-green-600 to-green-700',
      description: 'Administra los establecimientos del sistema'
    }] : []),
    {
      title: 'Fuentes',
      subtitle: 'Gestionar fuentes disponibles',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      onClick: () => navigate('/management/fonts'),
      gradient: 'from-purple-600 to-purple-700',
      description: 'Administra las fuentes tipográficas'
    },
    {
      title: 'Tarifas',
      subtitle: 'Gestionar tarifas de establecimientos',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: () => navigate('/management/tariffs'),
      gradient: 'from-orange-600 to-orange-700',
      description: 'Sincroniza y administra tarifas desde Hioffice'
    },
    {
      title: 'Banners',
      subtitle: 'Gestionar banners de establecimientos',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      onClick: () => navigate('/management/banners'),
      gradient: 'from-pink-600 to-pink-700',
      description: 'Administra los banners promocionales'
    },
    {
      title: 'Banners Inteligentes',
      subtitle: 'Banners configurables con productos',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      onClick: () => navigate('/management/smart-banners'),
      gradient: 'from-cyan-600 to-cyan-700',
      description: 'Crea banners inteligentes configurables con productos y contenido personalizado'
    },
    {
      title: 'Productos',
      subtitle: 'Visualizar productos de establecimientos',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      onClick: () => navigate('/management/products'),
      gradient: 'from-indigo-600 to-indigo-700',
      description: 'Consulta productos por establecimiento y tarifa'
    },
    {
      title: 'Extras',
      subtitle: 'Gestionar comentarios de exportación',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      ),
      onClick: () => navigate('/management/extras'),
      gradient: 'from-emerald-600 to-emerald-700',
      description: 'Sincroniza y administra comentarios desde Hioffice'
    },
    {
      title: 'Pantallas',
      subtitle: 'Configurar pantallas de visualización',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      onClick: () => navigate('/management/screens'),
      gradient: 'from-teal-600 to-teal-700',
      description: 'Gestiona la configuración de pantallas de menú'
    },
    {
      title: 'Iconos',
      subtitle: 'Gestionar iconos globales de productos',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
      onClick: () => navigate('/management/icons'),
      gradient: 'from-yellow-600 to-yellow-700',
      description: 'Administra los iconos globales para productos (aguacate, picante, recomendado)'
    },
    ...(canView('dashboard.monitoring_card') ? [{
      title: 'Monitoreo',
      subtitle: 'Estadísticas y logs del sistema',
      icon: (
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      onClick: () => navigate('/management/monitoring'),
      gradient: 'from-red-600 to-red-700',
      description: 'Monitorea el rendimiento y logs del sistema'
    }] : [])
  ];

  return (
    <DashboardLayout
      title="HioMenu"
      subtitle="Panel de Gestión"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 h-[calc(100vh-200px)] overflow-hidden">
        {/* Columna Izquierda: Accesos Rápidos - Grid Responsivo */}
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Accesos Rápidos</h2>
            <span className="text-xs text-gray-500 hidden sm:inline">{dashboardCards.length} módulos</span>
          </div>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              {dashboardCards.map((card, index) => (
                <Card 
                  key={index} 
                  hover 
                  onClick={card.onClick}
                  className="cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex-shrink-0 group"
                >
                  <CardContent className="p-4 sm:p-5 h-full flex flex-col items-center text-center">
                    <div className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${card.gradient} rounded-2xl mb-3 shadow-lg flex-shrink-0 transform transition-transform duration-300 group-hover:scale-110`}>
                      {card.icon}
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">{card.title}</h3>
                    <p className="text-xs text-gray-600 mb-2 line-clamp-1">{card.subtitle}</p>
                    <p className="text-xs text-gray-500 line-clamp-2 flex-1">{card.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Columna Derecha: Estadísticas Generales */}
        <div className="flex flex-col h-full overflow-hidden">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex-shrink-0">Estadísticas Generales</h2>
          <div className="flex-1 overflow-y-auto pr-2">
            
            {loadingStats ? (
              <Card>
                <CardContent>
                  <div className="p-8 sm:p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                    <p className="text-sm sm:text-base text-gray-600">Cargando estadísticas...</p>
                  </div>
                </CardContent>
              </Card>
            ) : stats ? (
              <div className="space-y-3">
                {/* Estadísticas para admin/management */}
                {(user?.role === 'admin' || user?.role === 'management') && (
                  <>
                    {/* Grid Principal de Estadísticas - Cards más pequeños */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {/* Clientes */}
                      {canView('dashboard.clients_card') && (
                        <Card className="hover:shadow-lg transition-shadow">
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </div>
                              <button
                                onClick={() => navigate('/management/clients')}
                                className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                              >
                                →
                              </button>
                            </div>
                            <h3 className="text-xs font-semibold text-gray-600 mb-1">Clientes</h3>
                            <p className="text-xl font-bold text-gray-900 mb-2">{stats.total_clients ?? 0}</p>
                            <div className="hidden sm:grid grid-cols-3 gap-1.5 pt-2 border-t border-gray-100">
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5">Activos</p>
                                <p className="text-sm font-semibold text-green-600">{stats.active_clients ?? 0}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5">Inactivos</p>
                                <p className="text-sm font-semibold text-red-600">{stats.inactive_clients ?? 0}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-500 mb-0.5">Este mes</p>
                                <p className="text-sm font-semibold text-blue-600">{stats.this_month_clients ?? 0}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Establecimientos */}
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                            <button
                              onClick={() => navigate('/management/establishments')}
                              className="text-green-600 hover:text-green-700 text-xs font-medium"
                            >
                              →
                            </button>
                          </div>
                          <h3 className="text-xs font-semibold text-gray-600 mb-1">Establecimientos</h3>
                          <p className="text-xl font-bold text-gray-900 mb-2">{stats.total_establishments ?? 0}</p>
                          <div className="hidden sm:grid grid-cols-3 gap-1.5 pt-2 border-t border-gray-100">
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Activos</p>
                              <p className="text-sm font-semibold text-green-600">{stats.active_establishments ?? 0}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Inactivos</p>
                              <p className="text-sm font-semibold text-red-600">{stats.inactive_establishments ?? 0}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Este mes</p>
                              <p className="text-sm font-semibold text-blue-600">{stats.this_month_establishments ?? 0}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Usuarios - Solo visible para admin */}
                      {canView('dashboard.users_card') && (
                        <Card className="hover:shadow-lg transition-shadow">
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                              </div>
                            </div>
                            <h3 className="text-xs font-semibold text-gray-600 mb-1">Usuarios</h3>
                            <p className="text-xl font-bold text-gray-900 mb-2">{stats.total_users ?? 0}</p>
                            <div className="hidden sm:block pt-2 border-t border-gray-100">
                              <p className="text-[10px] text-gray-500 mb-0.5">Activos</p>
                              <p className="text-sm font-semibold text-green-600">{stats.active_users ?? 0}</p>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Pantallas */}
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <button
                              onClick={() => navigate('/management/screens')}
                              className="text-teal-600 hover:text-teal-700 text-xs font-medium"
                            >
                              →
                            </button>
                          </div>
                          <h3 className="text-xs font-semibold text-gray-600 mb-1">Pantallas</h3>
                          <p className="text-xl font-bold text-gray-900 mb-2">{stats.total_screens ?? 0}</p>
                          <div className="hidden sm:grid grid-cols-3 gap-1.5 pt-2 border-t border-gray-100">
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Activas</p>
                              <p className="text-sm font-semibold text-green-600">{stats.active_screens ?? 0}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Inactivas</p>
                              <p className="text-sm font-semibold text-red-600">{stats.inactive_screens ?? 0}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Este mes</p>
                              <p className="text-sm font-semibold text-blue-600">{stats.this_month_screens ?? 0}</p>
                            </div>
                          </div>
                          {stats.total_accesses !== undefined && stats.total_accesses > 0 && (
                            <div className="hidden sm:block pt-2 border-t border-gray-100 mt-2">
                              <p className="text-[10px] text-gray-500 mb-0.5">Total accesos</p>
                              <p className="text-sm font-semibold text-indigo-600">{stats.total_accesses.toLocaleString()}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Productos */}
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <button
                              onClick={() => navigate('/management/products')}
                              className="text-indigo-600 hover:text-indigo-700 text-xs font-medium"
                            >
                              →
                            </button>
                          </div>
                          <h3 className="text-xs font-semibold text-gray-600 mb-1">Productos</h3>
                          <p className="text-xl font-bold text-gray-900">{stats.total_products?.toLocaleString() ?? 0}</p>
                        </CardContent>
                      </Card>

                      {/* Banners */}
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <button
                              onClick={() => navigate('/management/banners')}
                              className="text-pink-600 hover:text-pink-700 text-xs font-medium"
                            >
                              →
                            </button>
                          </div>
                          <h3 className="text-xs font-semibold text-gray-600 mb-1">Banners</h3>
                          <p className="text-xl font-bold text-gray-900">{stats.total_banners ?? 0}</p>
                        </CardContent>
                      </Card>

                      {/* Fuentes */}
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                              </svg>
                            </div>
                            <button
                              onClick={() => navigate('/management/fonts')}
                              className="text-purple-600 hover:text-purple-700 text-xs font-medium"
                            >
                              →
                            </button>
                          </div>
                          <h3 className="text-xs font-semibold text-gray-600 mb-1">Fuentes</h3>
                          <p className="text-xl font-bold text-gray-900">{stats.total_fonts ?? 0}</p>
                        </CardContent>
                      </Card>

                      {/* Iconos */}
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                            </div>
                            <button
                              onClick={() => navigate('/management/icons')}
                              className="text-yellow-600 hover:text-yellow-700 text-xs font-medium"
                            >
                              →
                            </button>
                          </div>
                          <h3 className="text-xs font-semibold text-gray-600 mb-1">Iconos</h3>
                          <p className="text-xl font-bold text-gray-900">{stats.total_icons ?? 0}</p>
                        </CardContent>
                      </Card>

                      {/* Tarifas */}
                      <Card className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <button
                              onClick={() => navigate('/management/tariffs')}
                              className="text-orange-600 hover:text-orange-700 text-xs font-medium"
                            >
                              →
                            </button>
                          </div>
                          <h3 className="text-xs font-semibold text-gray-600 mb-1">Tarifas</h3>
                          <p className="text-xl font-bold text-gray-900">{stats.total_tariffs ?? 0}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Alertas y Top Pantallas - Grid de 2 columnas */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3 mt-3">
                      {/* Alertas - Establecimientos sin pantallas */}
                      {stats.establishments_without_screens !== undefined && stats.establishments_without_screens > 0 && (
                        <Card className="border-2 border-amber-300 bg-amber-50">
                          <CardContent className="p-2 sm:p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xs font-semibold text-amber-800">Atención</h3>
                                <p className="text-xs font-bold text-amber-900 truncate">
                                  {stats.establishments_without_screens} sin pantallas
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Alertas - Establecimientos sin tarifas */}
                      {stats.establishments_without_tariffs !== undefined && stats.establishments_without_tariffs > 0 && (
                        <Card className="border-2 border-red-300 bg-red-50">
                          <CardContent className="p-2 sm:p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xs font-semibold text-red-800">Acción</h3>
                                <p className="text-xs font-bold text-red-900 truncate">
                                  {stats.establishments_without_tariffs} sin tarifas
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Top Pantallas Más Accedidas */}
                      {stats.top_screens && stats.top_screens.length > 0 && (
                        <Card className="lg:col-span-2">
                          <CardContent className="p-2 sm:p-3">
                            <h3 className="text-xs font-semibold text-gray-600 mb-2">Top Pantallas</h3>
                            <div className="space-y-1.5">
                              {stats.top_screens.map((screen, index) => (
                                <div key={screen.id} className="flex items-center justify-between p-1.5 bg-gray-50 rounded">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <div className="w-5 h-5 bg-gradient-to-br from-teal-500 to-teal-600 rounded flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold">
                                      {index + 1}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-semibold text-gray-900 truncate">{screen.name}</p>
                                      <p className="text-[10px] text-gray-500 truncate">{screen.establishment_name}</p>
                                    </div>
                                  </div>
                                  <div className="text-right flex-shrink-0 ml-2">
                                    <p className="text-xs font-bold text-teal-600">{screen.access_count.toLocaleString()}</p>
                                    <p className="text-[10px] text-gray-500">accesos</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </>
                )}

                {/* Estadísticas para client */}
                {user?.role === 'client' && (
                  <>
                    {/* Establecimientos del cliente */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Establecimientos</h3>
                              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_establishments ?? 0}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => navigate('/management/establishments')}
                            className="text-green-600 hover:text-green-700 text-xs sm:text-sm font-medium flex-shrink-0 ml-2"
                          >
                            <span className="hidden sm:inline">Ver todos →</span>
                            <span className="sm:hidden">→</span>
                          </button>
                        </div>
                        <div className="hidden sm:grid grid-cols-3 gap-2 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-100">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Activos</p>
                            <p className="text-base sm:text-lg font-semibold text-green-600">{stats.active_establishments ?? 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Inactivos</p>
                            <p className="text-base sm:text-lg font-semibold text-red-600">{stats.inactive_establishments ?? 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Este mes</p>
                            <p className="text-base sm:text-lg font-semibold text-blue-600">{stats.this_month_establishments ?? 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pantallas */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Pantallas</h3>
                              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_screens ?? 0}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Productos */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Productos</h3>
                              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_products ?? 0}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Tarifas */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Tarifas</h3>
                              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_tariffs ?? 0}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Estadísticas para establishment */}
                {user?.role === 'establishment' && (
                  <>
                    {/* Establecimiento */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Establecimiento</h3>
                              <p className="text-sm sm:text-base font-bold text-gray-900 truncate">{stats.establishment_name ?? 'N/A'}</p>
                              <p className={`text-xs mt-1 ${stats.establishment_active ? 'text-green-600' : 'text-red-600'}`}>
                                {stats.establishment_active ? 'Activo' : 'Inactivo'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pantallas */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Pantallas</h3>
                              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_screens ?? 0}</p>
                            </div>
                          </div>
                        </div>
                        <div className="hidden sm:grid grid-cols-2 gap-2 sm:gap-4 pt-3 sm:pt-4 border-t border-gray-100">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Activas</p>
                            <p className="text-base sm:text-lg font-semibold text-green-600">{stats.active_screens ?? 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Inactivas</p>
                            <p className="text-base sm:text-lg font-semibold text-red-600">{stats.inactive_screens ?? 0}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Productos */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Productos</h3>
                              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_products ?? 0}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Tarifas */}
                    <Card>
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-xs sm:text-sm font-semibold text-gray-600">Tarifas</h3>
                              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_tariffs ?? 0}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            ) : (
              <Card>
                <CardContent>
                  <div className="p-12 text-center">
                    <p className="text-gray-600">No se pudieron cargar las estadísticas</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
